import type { ProgramSlideImageCropV1 } from '../types/programContentSlideCanvas';

/** 캔버스 정규 좌표에서 가로·세로 비율(aspect = width/height)에 맞는 높이 */
export function normFrameHeightForAspect(
  w: number,
  y: number,
  aspect: number,
  rootW: number,
  rootH: number
): number {
  const h = (w * rootW) / (aspect * rootH);
  return Math.min(1 - y, Math.max(0.05, Math.round(h * 10000) / 10000));
}

/** object-fit: cover 와 동일 — 원본에서 프레임 비율에 맞게 가운데 크롭할 소스 사각형(픽셀) */
export function coverCropSourceRect(
  srcW: number,
  srcH: number,
  frameAspect: number
): { sx: number; sy: number; sw: number; sh: number } {
  if (srcW <= 0 || srcH <= 0 || frameAspect <= 0) {
    return { sx: 0, sy: 0, sw: Math.max(1, srcW), sh: Math.max(1, srcH) };
  }
  const srcAspect = srcW / srcH;
  if (srcAspect > frameAspect) {
    const sh = srcH;
    const sw = srcH * frameAspect;
    return { sx: (srcW - sw) / 2, sy: 0, sw, sh };
  }
  const sw = srcW;
  const sh = srcW / frameAspect;
  return { sx: 0, sy: (srcH - sh) / 2, sw, sh };
}

/** 크롭 편집(프레임에 fill) 기준 — cover 와 동일한 초기 crop (0~1) */
export function coverCropFrameFrac(
  naturalW: number,
  naturalH: number,
  frameAspect: number
): ProgramSlideImageCropV1 {
  if (naturalW <= 0 || naturalH <= 0) {
    return { x: 0, y: 0, w: 1, h: 1 };
  }
  const { sx, sy, sw, sh } = coverCropSourceRect(naturalW, naturalH, frameAspect);
  return {
    x: sx / naturalW,
    y: sy / naturalH,
    w: sw / naturalW,
    h: sh / naturalH,
  };
}

function loadImageElement(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('이미지를 불러오지 못했습니다.'));
    img.src = src;
  });
}

/** 프레임 crop(0~1, fill 매핑) 영역을 새 비트맵으로 굽기 */
export async function bakeFrameCropToBlob(
  src: string,
  naturalW: number,
  naturalH: number,
  crop: ProgramSlideImageCropV1
): Promise<Blob> {
  const img = await loadImageElement(src);
  const sx = Math.max(0, Math.min(naturalW - 1, Math.round(crop.x * naturalW)));
  const sy = Math.max(0, Math.min(naturalH - 1, Math.round(crop.y * naturalH)));
  const sw = Math.max(1, Math.min(naturalW - sx, Math.round(crop.w * naturalW)));
  const sh = Math.max(1, Math.min(naturalH - sy, Math.round(crop.h * naturalH)));
  const canvas = document.createElement('canvas');
  canvas.width = sw;
  canvas.height = sh;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('이미지 처리에 실패했습니다.');
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('이미지 처리에 실패했습니다.'))),
      'image/jpeg',
      0.94
    );
  });
}
